import { api } from './api';

export interface StorefrontCheckoutRequest {
  clinic_id: string;
  patient_id: string;
  practitioner_id: string | undefined;
  program_id?: string;
  priority?: number; // Priority applies to all medications (-1: Low, 1: Normal, 2: Elevated, 3: Urgent)
  clinic_program_option_id?: string; // Guided clinic: triggers payment hold instead of immediate charge
  skip_payment?: boolean; // Skip payment at checkout; collect during intake instead
  medicationRequests: Array<{
    additionalContext: string;
    dose: string;
    medication: string;
    quantity: number | string;
    reason: string;
    refills?: string | null;
    sig: string;
    unit: string;
    daysSupply?: string | null;
    flavor?: string | null;
    notes?: string | null;
    doNotFill?: boolean;
    doNotRefill?: boolean;
    patientContactRequested?: boolean;
    refillNeeded?: boolean;
    product_id?: string | null;
    template_id?: string | null;
    program_id?: string | null; // Program ID for subscription grouping
    discount_cents?: number; // Per-line-item discount in cents (per_unit_discount * quantity)
  }>;
  token: string;
  amount: number;
  fraud_session_id: string;
  is_subscription: boolean;
  allergyWarningsAcknowledged?: boolean;
  payment_method_id?: string;
  shipping?: {
    carrier_id: string;
    carrier_name: string;
    service_id: string;
    service_name: string;
    cost_cents: number;
    original_cost_cents: number;
    is_free_shipment: boolean;
    free_shipment_type: string | null;
  };
  subscription?: {
    frequency: "weekly" | "monthly" | "quarterly";
    duration?: number; // e.g., months
    autoRenew: boolean;
  };
  // Referrer attribution (guided checkout only — ignored otherwise).
  referring_user_id?: string | null;
  commission_rate_bps?: number | null;
}

export interface SubscriptionInfo {
  id: string;
  program_id: string; // ClinicProgram.id
  amount: number;
  medications_count: number;
}

export interface StorefrontCheckoutResponse {
  success: boolean;
  prescription_id?: string;
  payment_id?: string;
  transaction_id?: string;
  data?: {
    id: string;
    prescription_id?: string;
    payment_id?: string;
    subscription_id?: string;
    guided_order_id?: string;
    // New: multiple subscriptions can be created (one per program)
    subscriptions?: SubscriptionInfo[];
  };
  message?: string;
  error?: string;
}

class StorefrontCheckoutService {
  /**
   * Process storefront checkout - creates prescription and processes payment
   */
  async processStorefrontCheckout(
    checkoutData: StorefrontCheckoutRequest
  ): Promise<StorefrontCheckoutResponse> {
    const response = await api.post<StorefrontCheckoutResponse>(
      '/clinic/storefront/save-medication-request',
      checkoutData
    );
    return response;
  }

  /**
   * Get prescription confirmation details
   */
  async getPrescriptionConfirmation(prescriptionId: string): Promise<any> {
    try {
      const response = await api.get(`/prescriptions/${prescriptionId}/confirmation`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching prescription confirmation:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch prescription details');
    }
  }

  /**
   * Validate checkout data before submission
   */
  validateCheckoutData(data: StorefrontCheckoutRequest): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.patient_id) errors.push('Patient selection is required');
    // Guided clinic checkouts (clinic_program_option_id present) do not require a practitioner.
    if (!data.clinic_program_option_id && !data.practitioner_id) errors.push('Practitioner selection is required');
    // Payment token not required when skip_payment is true (deferred to intake)
    if (!data.skip_payment && !data.token) errors.push('Payment token is required');
    if (!data.skip_payment && !data.fraud_session_id) errors.push('Security session is required');
    if (!data.clinic_program_option_id && (!data.medicationRequests || data.medicationRequests.length === 0))
      errors.push('Cart cannot be empty');
    if (data.amount <= 0) errors.push('Total amount must be greater than 0');

    if (data.clinic_program_option_id) {
      return { isValid: errors.length === 0, errors };
    }

    // Subscription-specific validation
    if (data.is_subscription) {
      if (!data.payment_method_id) {
        errors.push('Payment method ID is required for subscriptions');
      }

      // Check if at least one medication has subscription details
      const hasSubscriptionMedication = data.medicationRequests.some(req => req.subscription);
      if (!hasSubscriptionMedication) {
        errors.push('At least one medication must have subscription details when is_subscription is true');
      }
    }

    // Validate each medication request
    data.medicationRequests?.forEach((medRequest, index) => {
      if (!medRequest.additionalContext)
        errors.push(`Item ${index + 1}: Additional context is required`);
      if (!medRequest.dose) errors.push(`Item ${index + 1}: Dose is required`);
      if (!medRequest.medication) errors.push(`Item ${index + 1}: Medication name is required`);
      if (!medRequest.quantity) errors.push(`Item ${index + 1}: Quantity is required`);
      if (!medRequest.reason) errors.push(`Item ${index + 1}: Reason is required`);
      if (!medRequest.sig) errors.push(`Item ${index + 1}: Instructions (sig) are required`);
      if (!medRequest.unit) errors.push(`Item ${index + 1}: Unit is required`);

      // Validate subscription fields if present
      if (medRequest.subscription) {
        if (!medRequest.subscription.frequency) {
          errors.push(`Item ${index + 1}: Subscription frequency is required`);
        }
        if (!['weekly', 'monthly', 'quarterly'].includes(medRequest.subscription.frequency)) {
          errors.push(`Item ${index + 1}: Invalid subscription frequency. Must be weekly, monthly, or quarterly`);
        }
        if (medRequest.subscription.duration !== undefined && medRequest.subscription.duration <= 0) {
          errors.push(`Item ${index + 1}: Subscription duration must be greater than 0`);
        }
        if (typeof medRequest.subscription.autoRenew !== 'boolean') {
          errors.push(`Item ${index + 1}: Subscription autoRenew must be a boolean value`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

export const storefrontCheckoutService = new StorefrontCheckoutService();
export default storefrontCheckoutService;
